import { useCallback, useEffect, useRef } from 'react';
import { useNavigationType } from 'react-router-dom';

const cache = new Map();

export const useRestoredSearchState = (key) => {
    const navType = useNavigationType();
    const ref = useRef(undefined);
    if (ref.current === undefined) {
        if (navType === 'POP') {
            ref.current = loadSearchState(key);
        } else {
            clearSearchState(key);
            ref.current = null;
        }
    }
    return ref.current;
};

export const saveSearchState = (key, state) => {
    cache.set(key, state);
};

export const loadSearchState = (key) => {
    return cache.get(key) || null;
};

export const clearSearchState = (key) => {
    cache.delete(key);
};

export const clearAllSearchStates = () => {
    cache.clear();
};

export const useSearchStateCache = (key, state, options = {}) => {
    const { enabled = true } = options;
    const navType = useNavigationType();

    const restoredRef = useRef(undefined);
    if (restoredRef.current === undefined) {
        restoredRef.current = navType === 'POP' ? loadSearchState(key) : null;
    }
    const restored = restoredRef.current;

    const skipInitialFetchRef = useRef(Boolean(restored));
    const stateRef = useRef(state);
    stateRef.current = state;

    useEffect(() => {
        if (!enabled) return;
        saveSearchState(key, stateRef.current);
    });

    const consumeInitialFetchSkip = useCallback(() => {
        if (!skipInitialFetchRef.current) return false;
        skipInitialFetchRef.current = false;
        return true;
    }, []);

    const restoredIdentityRef = useRef(undefined);
    const shouldSkipRestoredFetch = useCallback((identity) => {
        if (!restored) return false;
        if (restoredIdentityRef.current === undefined) {
            restoredIdentityRef.current = identity;
            return true;
        }
        if (restoredIdentityRef.current === identity) return true;
        restoredIdentityRef.current = null;
        return false;
    }, [restored]);

    const clear = useCallback(() => {
        clearSearchState(key);
        skipInitialFetchRef.current = false;
        restoredIdentityRef.current = null;
    }, [key]);

    return { restored, consumeInitialFetchSkip, shouldSkipRestoredFetch, clear };
};
